import React, { useMemo } from "react";
import Table from "../../../components/TableV2";
import TitleButtons from "../../../components/TitleButtons";
import { useEmpresasCliente } from "./empresasCadastradasData";
import { useNavigate } from "react-router-dom";

// ======================= Tipos =======================
type Cliente = {
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  email?: string | null;
  telefone?: string | null;
  responsaveis_nomes?: string[] | null;
};

type Row = React.ReactNode[];

// ======================= Utils =======================
function onlyDigits(s: string) {
  return String(s || "").replace(/\D+/g, "");
}

function formatCNPJ(cnpj: string) {
  const d = onlyDigits(cnpj).padStart(14, "0").slice(-14);
  // 00.000.000/0000-00
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

/** Normaliza quando a página receber dados "crus" (array direto ou {data:[]}) */
function normalizeClientes(raw: any): Cliente[] {
  if (!raw) return [];
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : [];

  return (arr as any[]).map((x) => ({
    cnpj: x.cnpj ?? x.CNPJ ?? "",
    razao_social:
      x.razao_social ?? x.razaoSocial ?? x.razao ?? x.razao_social_nome ?? "",
    nome_fantasia:
      x.nome_fantasia ?? x.nomeFantasia ?? x.fantasia ?? x.nome ?? "",
    email: x.email ?? x.mail ?? null,
    telefone: x.telefone ?? x.tel ?? x.phone ?? null,
    responsaveis_nomes: Array.isArray(x.responsaveis_nomes)
      ? x.responsaveis_nomes.map(String)
      : x.responsaveis_nomes ?? null,
  }));
}

// ======================= Página =======================
const EmpresasClientes: React.FC = () => {
  // Agora com a última coluna "C. Responsável"
  const headers = useMemo(
    () => ["CNPJ", "Razão Social", "Nome Fantasia", "C. Responsável"],
    []
  );

  //            CNPJ      Razão Social   Nome Fantasia   C. Responsável
  const columnWidths = useMemo(() => ["180px", "360px", "260px", "240px"], []);

  // Hook de dados
  const hook = useEmpresasCliente?.();
  const data = normalizeClientes((hook as any)?.data ?? hook);

  const rows: Row[] = useMemo(() => {
    return data.map((c) => {
      const responsavel =
        c.responsaveis_nomes && c.responsaveis_nomes.length
          ? c.responsaveis_nomes[0] // pega o primeiro responsável
          : "-";

      return [
        formatCNPJ(c.cnpj || ""),
        c.razao_social || "-",
        c.nome_fantasia || "-",
        responsavel,
      ];
    });
  }, [data]);

  const navigate = useNavigate();

  return (
    <div>
      <TitleButtons fullBleedLine title="Coordenação - Empresas/clientes" />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        responsiveWidths={{
          // IMPORTANTE: as chaves são 1-based no Table (1ª coluna = 1)
          1300: {
            1: "300px", // CNPJ
            2: "320px", // Razão Social
            3: "240px", // Nome Fantasia
            4: "220px", // C. Responsável
          },
          1000: {
            1: "180px", // CNPJ
            2: "260px", // Razão Social
            3: "0px", // esconde Nome Fantasia em telas muito estreitas
            4: "200px", // C. Responsável
          },
        }}
      />
    </div>
  );
};

export default EmpresasClientes;
